/**
 * Dansk talformatering. Eneste sted i kodebasen der maa formatere tal til visning -
 * jf. CLAUDE.md. Skriv aldrig ad-hoc formatering i en komponent.
 */

const NBSP = ' ';

/**
 * Tusindtalsseparator '.', decimalseparator ','.
 * Intl med da-DK giver praecis det, men vi laaser decimalerne eksplicit,
 * saa output er stabilt paa tvaers af ICU-versioner.
 */
export function formatNumber(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(value);
}

/** Timer vises altid med to decimaler: "186,50". */
export function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return formatNumber(value, 2);
}

/**
 * Procent med haardt mellemrum foer tegnet: "68 %".
 * Haardt mellemrum, saa tal og tegn aldrig brydes over to linjer.
 */
export function formatPercent(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, decimals)}${NBSP}%`;
}

/**
 * En afvigelse med fortegn: "+5,0", "−6,0" og "0,0". Et rigtigt minustegn (U+2212), ikke en
 * bindestreg - storskaermenes krav, og det er lige saa bredt som plusset, saa tallene flugter.
 */
export function formatSigned(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = formatNumber(Math.abs(value), decimals);
  const zero = formatNumber(0, decimals);
  if (text === zero) return text;
  return `${value > 0 ? '+' : '−'}${text}`;
}

/** Heltal uden decimaler: "1.245". */
export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return formatNumber(value, 0);
}

/**
 * Et tal, som det tastes i et felt - det omvendte af `formatNumber`. Punktum er tusindtal og komma
 * decimal, saa "1.500.000", "1500000", "1.500.000,50" og "1500000,5" er alle gyldige. Et punktum,
 * der ikke staar foran tre cifre, laeses som decimal: "1500.5" er 1500,5.
 *
 * Mellemrum og et "kr" til sidst ignoreres. NaN, naar teksten ikke er et tal.
 */
export function parseNumber(text: string): number {
  const cleaned = text.replace(/\s/g, '').replace(/kr\.?$/i, '');
  if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(cleaned) || /^-?\d+(,\d+)?$/.test(cleaned)) {
    return Number(cleaned.replace(/\./g, '').replace(',', '.'));
  }
  if (/^-?\d+\.\d+$/.test(cleaned)) return Number(cleaned);
  return Number.NaN;
}
